import { useState } from "react";
import { useForm } from "react-hook-form";
import { CheckCircle2, AlertCircle, Loader2, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { http, errorMessage } from "../../lib/api";
import { COUNTRIES, PARTNERSHIP_TYPES, SERVICES } from "../../data/content";
import { Btn } from "./Ui";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RX = /^[+()\-\s\d]{6,20}$/;

const Field = ({ label, name, error, required, children }) => (
  <label className="block" htmlFor={name}>
    <span className="mb-2 block text-sm font-semibold text-[#102B50]">
      {label} {required && <span className="text-[#2878E5]">*</span>}
    </span>
    {children}
    {error && <span data-testid={`error-${name}`} className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600"><AlertCircle className="h-3.5 w-3.5" />{error}</span>}
  </label>
);

const SelectWrap = ({ children }) => (
  <div className="relative">
    {children}
    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
  </div>
);

const Success = ({ onReset, cta }) => (
  <motion.div data-testid="form-success" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-start rounded-2xl bg-[#E8F1FD] p-8 sm:p-10">
    <CheckCircle2 className="h-12 w-12 text-[#2878E5]" strokeWidth={1.6} />
    <h3 className="mt-5 font-display text-2xl font-bold text-[#102B50]">Thank you, your enquiry has been received.</h3>
    <p className="mt-3 text-[#4A5D78]">Our partnerships team will review your details and get back to you shortly.</p>
    <button onClick={onReset} data-testid="form-reset-btn" className="mt-6 text-sm font-semibold text-[#2878E5] hover:underline">{cta}</button>
  </motion.div>
);

export const EnquiryForm = ({ source = "contact", defaultType = "", submitLabel = "Send Enquiry" }) => {
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { partnership_type: defaultType, country: "", service: "", website: "" },
  });

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await http.post("/enquiries", { ...values, source });
      setDone(true);
      reset();
    } catch (e) {
      setServerError(errorMessage(e, "We couldn’t send your enquiry right now. Please try again in a moment."));
    }
  };

  if (done) return <Success onReset={() => setDone(false)} cta="Send another enquiry" />;

  const req = (msg) => ({ required: msg });
  return (
    <form data-testid={`enquiry-form-${source}`} onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-5 sm:grid-cols-2">
      <Field label="Full Name" name="full_name" required error={errors.full_name?.message}>
        <input id="full_name" data-testid="input-full-name" className="field" autoComplete="name" placeholder="Jane Smith" aria-invalid={!!errors.full_name}
          {...register("full_name", { ...req("Please enter your full name"), minLength: { value: 2, message: "Name looks too short" } })} />
      </Field>
      <Field label="Company Name" name="company" required error={errors.company?.message}>
        <input id="company" data-testid="input-company" className="field" autoComplete="organization" placeholder="Company Ltd." aria-invalid={!!errors.company}
          {...register("company", req("Please enter your company name"))} />
      </Field>
      <Field label="Business Email" name="email" required error={errors.email?.message}>
        <input id="email" type="email" data-testid="input-email" className="field" autoComplete="email" placeholder="name@company.com" aria-invalid={!!errors.email}
          {...register("email", { ...req("Please enter your business email"), pattern: { value: EMAIL_RX, message: "Please enter a valid email address" } })} />
      </Field>
      <Field label="Phone Number" name="phone" error={errors.phone?.message}>
        <input id="phone" type="tel" data-testid="input-phone" className="field" autoComplete="tel" placeholder="+44 20 0000 0000" aria-invalid={!!errors.phone}
          {...register("phone", { pattern: { value: PHONE_RX, message: "Please enter a valid phone number" } })} />
      </Field>
      <Field label="Country" name="country" required error={errors.country?.message}>
        <SelectWrap>
          <select id="country" data-testid="select-country" className="field appearance-none pr-10" aria-invalid={!!errors.country} {...register("country", req("Please select your country"))}>
            <option value="" disabled>Select country</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </SelectWrap>
      </Field>
      <Field label="Partnership Type" name="partnership_type" required error={errors.partnership_type?.message}>
        <SelectWrap>
          <select id="partnership_type" data-testid="select-partnership-type" className="field appearance-none pr-10" aria-invalid={!!errors.partnership_type} {...register("partnership_type", req("Please choose a partnership type"))}>
            <option value="" disabled>Select type</option>
            {PARTNERSHIP_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </SelectWrap>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Interested Service" name="service">
          <SelectWrap>
            <select id="service" data-testid="select-service" className="field appearance-none pr-10" {...register("service")}>
              <option value="">Not sure yet</option>
              {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            </select>
          </SelectWrap>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Message" name="message" required error={errors.message?.message}>
          <textarea id="message" rows={5} data-testid="input-message" className="field resize-y" placeholder="Tell us about your goals, target markets and preferred campaign models." aria-invalid={!!errors.message}
            {...register("message", { ...req("Please add a short message"), minLength: { value: 10, message: "Please write at least 10 characters" } })} />
        </Field>
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden {...register("website")} />
      {serverError && (
        <div data-testid="form-error" role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:col-span-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{serverError}
        </div>
      )}
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">By submitting, you agree to our <a href="/privacy-policy" className="underline hover:text-[#2878E5]">Privacy Policy</a>.</p>
        <Btn type="submit" disabled={isSubmitting} arrow={!isSubmitting} data-testid="form-submit-btn" className="disabled:opacity-70">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}{isSubmitting ? "Sending..." : submitLabel}
        </Btn>
      </div>
    </form>
  );
};
